import { useEffect, useState } from "react";
import api from "@/lib/api";
import { API_URL } from "@/lib/api";
import { toast } from "sonner";
import {
  BookOpen, Download, KeyRound, Plus, Copy, RefreshCw,
  Trash2, ShieldCheck, ChevronRight, FileCode, Package,
  Server, Info,
} from "lucide-react";

/**
 * Developer Portal — the vendor-facing docs for the Fundle Loyalty API.
 * Renders the exhaustive field-by-field spec used to generate the PDF.
 */
export default function APIDocsPage() {
  const [ref, setRef] = useState(null);
  const [selected, setSelected] = useState(null);
  const [lang, setLang] = useState("curl");
  const [creds, setCreds] = useState([]);
  const [newCred, setNewCred] = useState(null);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", merchant_id: "", is_sandbox: true });

  const loadAll = async () => {
    try {
      const r = await fetch(`${API_URL}/pos/v1/loyalty-reference.json`).then((x) => x.json());
      const k = await api.get("/dev/loyalty-credentials");
      setRef(r);
      setCreds(k.data.credentials || []);
      if (!selected && r?.endpoints?.length) setSelected(r.endpoints[0].id);
    } catch (e) {
      toast.error("Failed to load: " + (e?.response?.data?.detail || e.message));
    }
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const endpoint = ref?.endpoints?.find((e) => e.id === selected);

  const download = (path, filename) => {
    const url = `${API_URL}${path}`;
    const a = document.createElement("a");
    a.href = url; a.download = filename || ""; a.target = "_blank";
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const createCred = async () => {
    if (!form.label.trim()) return toast.error("Label required");
    try {
      const r = await api.post("/dev/loyalty-credentials", form);
      setNewCred(r.data);
      setForm({ label: "", merchant_id: "", is_sandbox: true });
      setCreating(false);
      loadAll();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const revoke = async (id) => {
    if (!window.confirm("Revoke this credential? Vendors using it will fail immediately.")) return;
    try { await api.delete(`/dev/loyalty-credentials/${id}`); toast.success("Revoked"); loadAll(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  if (!ref) return <div className="p-10 text-white/60">Loading Developer Portal…</div>;

  const cats = Array.from(new Set(ref.endpoints.map((e) => e.category)));

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="dev-portal-page">
      <div className="flex items-start justify-between mb-4 gap-6 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/40">Developer Portal</div>
          <h1 className="font-display text-3xl text-white flex items-center gap-3 mt-1">
            <BookOpen className="w-6 h-6 kazo-text-champagne" />
            {ref.product} · v{ref.version}
          </h1>
          <p className="text-sm text-white/60 mt-2 max-w-3xl">
            The vendor-facing loyalty API surface. Every method is <code className="text-amber-300">POST</code>,
            every request is wrapped in <code className="text-amber-300">{"{objClass: {…}}"}</code>,
            every response is a <code className="text-amber-300">{"<MethodName>Result"}</code> envelope.
          </p>
          <div className="text-xs text-white/50 mt-2 flex flex-wrap gap-3">
            <span>Staging: <code className="text-amber-300">{ref.base_url_staging}</code></span>
            <span>Production: <code className="text-white/60">{ref.base_url_prod}</code></span>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a href={`${API_URL}/pos/v1/loyalty-docs.pdf`} target="_blank" rel="noreferrer"
             className="flex items-center gap-2 px-3 py-2 text-xs rounded-sm bg-amber-300 text-black font-medium hover:bg-amber-200"
             data-testid="btn-download-loyalty-pdf">
            <Download className="w-4 h-4" /> Full API PDF
          </a>
          <button onClick={() => download("/pos/v1/openapi.json", "fundle-pos-api-v1.openapi.json")}
                  className="flex items-center gap-2 px-3 py-2 text-xs rounded-sm border border-white/10 text-white hover:bg-white/10"
                  data-testid="btn-download-openapi">
            <FileCode className="w-4 h-4" /> OpenAPI
          </button>
          <button onClick={() => download("/pos/v1/postman.json", "fundle-pos-api-v1.postman_collection.json")}
                  className="flex items-center gap-2 px-3 py-2 text-xs rounded-sm border border-white/10 text-white hover:bg-white/10"
                  data-testid="btn-download-postman">
            <Package className="w-4 h-4" /> Postman
          </button>
        </div>
      </div>

      {/* CREDENTIALS STRIP */}
      <div className="rounded-sm border border-white/10 bg-black/30 p-4 mb-4" data-testid="loyalty-creds-panel">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Vendor Credentials (userid / pwd) · {creds.length} issued
          </div>
          <button onClick={() => setCreating(true)} className="flex items-center gap-1 px-3 py-1.5 text-xs bg-white text-black rounded-sm"
                  data-testid="btn-new-loyalty-cred">
            <Plus className="w-3.5 h-3.5" /> New Credential
          </button>
        </div>

        {creating && (
          <div className="p-3 mb-3 rounded-sm border border-amber-300/30 bg-amber-300/5" data-testid="new-cred-form">
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Label</label>
                <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                       placeholder="e.g. Genesis · Mumbai sandbox"
                       className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                       data-testid="input-cred-label" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-white/40">Merchant ID (optional)</label>
                <input value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                       placeholder="MERCH-01"
                       className="mt-1 w-full bg-black/40 border border-white/10 px-3 py-2 text-sm rounded-sm"
                       data-testid="input-cred-merchant" />
              </div>
              <div className="flex items-end">
                <label className="text-xs text-white flex items-center gap-2">
                  <input type="checkbox" checked={form.is_sandbox}
                         onChange={(e) => setForm({ ...form, is_sandbox: e.target.checked })}
                         data-testid="chk-cred-sandbox" /> Sandbox credential
                </label>
              </div>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setCreating(false)} className="px-3 py-1.5 text-xs text-white/70 hover:text-white">Cancel</button>
              <button onClick={createCred} className="px-3 py-1.5 text-xs bg-white text-black rounded-sm" data-testid="btn-save-cred">Issue Credential</button>
            </div>
          </div>
        )}

        {newCred && (
          <div className="p-3 mb-3 rounded-sm border border-emerald-400/40 bg-emerald-500/5" data-testid="new-cred-result">
            <div className="text-emerald-300 text-xs mb-2 flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4" /> Credential issued. Copy the `pwd` now — it will NEVER be shown again.
            </div>
            <div className="grid md:grid-cols-2 gap-2 mb-2">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">userid</div>
                <code className="block text-xs bg-black/60 border border-white/10 rounded-sm p-2 text-amber-300 font-mono">{newCred.userid}</code>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40 mb-1">pwd</div>
                <div className="flex gap-1">
                  <code className="flex-1 text-xs bg-black/60 border border-white/10 rounded-sm p-2 text-amber-300 font-mono break-all">{newCred.pwd}</code>
                  <button onClick={() => { navigator.clipboard.writeText(newCred.pwd); toast.success("Copied"); }}
                          className="p-2 border border-white/10 rounded-sm hover:bg-white/10 text-white"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
            <button onClick={() => setNewCred(null)} className="text-[10px] text-white/50 hover:text-white">Dismiss</button>
          </div>
        )}

        {creds.length === 0 ? (
          <div className="text-white/40 text-xs p-2">No credentials yet. Issue one per vendor / merchant.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-white/40">
                <tr><th className="text-left p-1.5">Label</th><th className="text-left p-1.5">userid</th><th className="text-left p-1.5">pwd preview</th><th className="text-left p-1.5">Merchant</th><th className="text-left p-1.5">Env</th><th className="text-left p-1.5">Created</th><th className="text-left p-1.5">Last used</th><th className="text-left p-1.5">Status</th><th></th></tr>
              </thead>
              <tbody>
                {creds.map((k) => (
                  <tr key={k.id} className="border-t border-white/5 text-white/80" data-testid={`loyalty-cred-row-${k.id}`}>
                    <td className="p-1.5">{k.label}</td>
                    <td className="p-1.5 font-mono text-amber-300">{k.userid}</td>
                    <td className="p-1.5 font-mono text-white/50">{k.pwd_preview}</td>
                    <td className="p-1.5">{k.merchant_id || <span className="text-white/30">—</span>}</td>
                    <td className="p-1.5">{k.is_sandbox ? <span className="text-amber-300">sandbox</span> : <span className="text-emerald-300">live</span>}</td>
                    <td className="p-1.5 text-white/50">{k.created_at?.slice(0, 10)}</td>
                    <td className="p-1.5 text-white/50">{k.last_used_at?.slice(0, 16).replace("T", " ") || "—"}</td>
                    <td className="p-1.5">{k.revoked_at ? <span className="text-red-300">revoked</span> : <span className="text-emerald-300">active</span>}</td>
                    <td className="p-1.5">{!k.revoked_at && (<button onClick={() => revoke(k.id)} className="text-red-300 hover:text-red-200" data-testid={`btn-revoke-cred-${k.id}`}><Trash2 className="w-3.5 h-3.5" /></button>)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DOCS */}
      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        <div className="rounded-sm border border-white/10 bg-black/30 p-2 max-h-[75vh] overflow-y-auto" data-testid="dev-sidebar">
          {cats.map((cat) => (
            <div key={cat} className="mb-2">
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/40 px-2 py-1">{cat}</div>
              {ref.endpoints.filter((e) => e.category === cat).map((e) => (
                <button key={e.id} onClick={() => setSelected(e.id)}
                  className={`w-full text-left px-2 py-1.5 text-xs rounded-sm flex items-center gap-2 ${
                    selected === e.id ? "bg-white/10 text-white" : "text-white/70 hover:bg-white/5"
                  }`} data-testid={`endpoint-${e.id}`}>
                  <span className="text-[9px] font-mono uppercase font-semibold text-emerald-400">POST</span>
                  <span className="truncate">{e.method_name}</span>
                </button>
              ))}
            </div>
          ))}
          <div className="mt-3 pt-3 border-t border-white/10 px-2">
            <button onClick={() => setSelected("__auth__")} className={`w-full text-left text-xs py-1.5 flex items-center gap-2 ${selected === "__auth__" ? "text-white" : "text-white/60 hover:text-white"}`} data-testid="endpoint-auth">
              <KeyRound className="w-3.5 h-3.5" /> Authentication
            </button>
            <button onClick={() => setSelected("__conventions__")} className={`w-full text-left text-xs py-1.5 flex items-center gap-2 ${selected === "__conventions__" ? "text-white" : "text-white/60 hover:text-white"}`} data-testid="endpoint-conventions">
              <Info className="w-3.5 h-3.5" /> Conventions
            </button>
            <button onClick={() => setSelected("__errors__")} className={`w-full text-left text-xs py-1.5 flex items-center gap-2 ${selected === "__errors__" ? "text-white" : "text-white/60 hover:text-white"}`} data-testid="endpoint-errors">
              <ShieldCheck className="w-3.5 h-3.5" /> Error catalogue
            </button>
          </div>
        </div>

        <div className="rounded-sm border border-white/10 bg-black/30 p-6 min-h-[400px]" data-testid="dev-detail">
          {selected === "__auth__" ? <AuthPanel ref_={ref} />
          : selected === "__conventions__" ? <ConventionsPanel ref_={ref} />
          : selected === "__errors__" ? <ErrorPanel errors={ref.errors} />
          : endpoint ? <EndpointDetail ep={endpoint} baseUrl={ref.base_url_staging} lang={lang} setLang={setLang} />
          : null}
        </div>
      </div>
    </div>
  );
}


function EndpointDetail({ ep, baseUrl, lang, setLang }) {
  const url = `${baseUrl}/${ep.method_name}`;
  const snippet = buildSnippet(lang, url, ep.sample_request);
  return (
    <div>
      <div className="flex items-center gap-3 mb-2">
        <span className="text-[10px] font-mono uppercase font-semibold px-1.5 py-0.5 rounded-sm border border-emerald-400/40 text-emerald-300">POST</span>
        <code className="text-white text-base font-mono break-all">{ep.method_name}</code>
      </div>
      <h2 className="font-display text-2xl text-white mb-1">{ep.summary}</h2>
      <div className="text-[11px] uppercase tracking-widest text-champagne text-white/40 mb-3">{ep.category}</div>
      <p className="text-sm text-white/70 mb-4 leading-relaxed">{ep.description}</p>

      {/* Request fields */}
      <SectionTitle>Request · objClass fields</SectionTitle>
      <FieldTable fields={ep.request_fields} />

      {ep.line_item_fields && (
        <>
          <SectionTitle>Nested · objClass.output[] line-item fields</SectionTitle>
          <FieldTable fields={ep.line_item_fields} />
        </>
      )}

      {/* Response fields */}
      <SectionTitle>Response · {ep.method_name}Result</SectionTitle>
      <FieldTable fields={ep.response_fields} narrow />

      {/* Code snippets */}
      <div className="mt-6">
        <div className="flex items-center gap-1 mb-1">
          {["curl", "javascript", "python"].map((l) => (
            <button key={l} onClick={() => setLang(l)}
              className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-sm ${lang === l ? "bg-white text-black" : "text-white/60 hover:text-white"}`}
              data-testid={`lang-${l}`}>{l}</button>
          ))}
          <button onClick={() => { navigator.clipboard.writeText(snippet); toast.success("Copied"); }}
                  className="ml-auto text-white/50 hover:text-white text-[10px] flex items-center gap-1"
                  data-testid="btn-copy-snippet">
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
        <pre className="text-[11px] bg-black/70 border border-white/10 rounded-sm p-3 text-emerald-200 overflow-auto whitespace-pre-wrap font-mono max-h-64">{snippet}</pre>
      </div>

      <SectionTitle>Sample response</SectionTitle>
      <pre className="text-[11px] bg-black/50 border border-white/10 rounded-sm p-3 text-white/85 overflow-auto max-h-80 whitespace-pre-wrap font-mono">
{JSON.stringify(ep.sample_response, null, 2)}
      </pre>
    </div>
  );
}


function SectionTitle({ children }) {
  return <div className="mt-6 mb-2 text-[10px] uppercase tracking-widest text-white/40">{children}</div>;
}


function FieldTable({ fields, narrow }) {
  return (
    <div className="overflow-x-auto border border-white/10 rounded-sm">
      <table className="w-full text-[12px]">
        <thead className="bg-burgundy/20 text-[10px] uppercase tracking-widest text-white/60">
          <tr>
            <th className="text-left p-2">Field</th>
            <th className="text-left p-2">Type</th>
            {!narrow && <th className="text-left p-2">Req.</th>}
            <th className="text-left p-2">Description</th>
            <th className="text-left p-2">Example</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((f, i) => (
            <tr key={f.name} className={i % 2 === 0 ? "bg-white/[0.02]" : ""}>
              <td className="p-2 font-mono text-amber-300 whitespace-nowrap">{f.name}</td>
              <td className="p-2 text-white/60 whitespace-nowrap">{f.type}</td>
              {!narrow && (
                <td className="p-2">
                  {f.required ? <span className="text-red-300 text-[10px] uppercase">Req</span>
                              : <span className="text-white/40 text-[10px] uppercase">Opt</span>}
                </td>
              )}
              <td className="p-2 text-white/75">{f.description}</td>
              <td className="p-2 font-mono text-white/50 text-[11px]">{
                f.example === "" ? <span className="text-white/25 italic">empty</span>
                : String(f.example ?? "").slice(0, 60)
              }</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function AuthPanel({ ref_ }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-white mb-4">Authentication</h2>
      <p className="text-sm text-white/70 mb-4">{ref_.auth.notes}</p>
      <FieldTable fields={ref_.auth.headers} />
      <SectionTitle>Sample cURL</SectionTitle>
      <pre className="text-[11px] bg-black/70 border border-white/10 rounded-sm p-3 text-emerald-200 overflow-auto whitespace-pre-wrap font-mono">
{`curl -X POST ${ref_.base_url_staging}/GET_CUSTOMER_TRANS_INFO \\
  -H 'userid: fun_user' \\
  -H 'pwd: 5100DE4B-06B6-4E84-8E4F-66A0DD0BF0D8' \\
  -H 'Content-Type: application/json' \\
  -d '{"objClass": {"customer_mobile": "9891331123"}}'`}
      </pre>
    </div>
  );
}


function ConventionsPanel({ ref_ }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-white mb-4">Conventions & Contract</h2>
      <ul className="text-sm text-white/75 space-y-2 leading-relaxed">
        {ref_.conventions.map((c, i) => (
          <li key={i} className="flex gap-2">
            <span className="text-amber-300 mt-0.5">•</span>
            <span dangerouslySetInnerHTML={{ __html: c.replace(/`([^`]+)`/g, "<code class='text-amber-300 font-mono text-xs'>$1</code>").replace(/\*\*([^*]+)\*\*/g, "<b>$1</b>") }} />
          </li>
        ))}
      </ul>
    </div>
  );
}


function ErrorPanel({ errors }) {
  return (
    <div>
      <h2 className="font-display text-2xl text-white mb-4">Error Catalogue</h2>
      <p className="text-sm text-white/70 mb-4">
        Fundle uses stable, machine-readable error codes. Always check <code className="text-amber-300">Success</code> in the response even when HTTP status is 200.
      </p>
      <table className="w-full text-sm border border-white/10">
        <thead className="text-[10px] uppercase tracking-widest text-white/40 bg-white/5">
          <tr><th className="text-left p-2">HTTP</th><th className="text-left p-2">Code / signal</th><th className="text-left p-2">When</th></tr>
        </thead>
        <tbody>
          {errors.map((e, i) => (
            <tr key={i} className="border-t border-white/5 text-white/80">
              <td className="p-2 font-mono">{e.http}</td>
              <td className="p-2 font-mono text-amber-300">{e.code}</td>
              <td className="p-2">{e.when}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}


function buildSnippet(lang, url, sample) {
  const bodyStr = JSON.stringify(sample, null, 2);
  if (lang === "curl") {
    return `curl -X POST '${url}' \\
  -H 'userid: fun_user' \\
  -H 'pwd: 5100DE4B-06B6-4E84-8E4F-66A0DD0BF0D8' \\
  -H 'Content-Type: application/json' \\
  -d '${JSON.stringify(sample)}'`;
  }
  if (lang === "javascript") {
    return `const r = await fetch("${url}", {
  method: "POST",
  headers: {
    "userid": "fun_user",
    "pwd": "5100DE4B-06B6-4E84-8E4F-66A0DD0BF0D8",
    "Content-Type": "application/json",
  },
  body: JSON.stringify(${bodyStr.replace(/\n/g, "\n  ")}),
});
const data = await r.json();`;
  }
  if (lang === "python") {
    return `import requests
r = requests.post(
    "${url}",
    headers={
        "userid": "fun_user",
        "pwd": "5100DE4B-06B6-4E84-8E4F-66A0DD0BF0D8",
        "Content-Type": "application/json",
    },
    json=${bodyStr.replace(/\n/g, "\n    ")},
)
data = r.json()`;
  }
  return "";
}
